/**
 * BT Buy Once link
 * Adds current variant with no selling_plan so one-time shipping rates apply.
 */
class BtBuyOnceLink extends HTMLElement {
  #data = {};
  #busy = false;

  connectedCallback() {
    try {
      this.#data = JSON.parse(this.querySelector('[data-bt-buy-once-data]')?.textContent || '{}');
    } catch {
      this.#data = {};
    }

    this.querySelector('[data-bt-buy-once-submit]')?.addEventListener('click', this.#onClick);
    document.addEventListener('variant:update', this.#onVariantUpdate);
    this.#refresh();
  }

  disconnectedCallback() {
    this.querySelector('[data-bt-buy-once-submit]')?.removeEventListener('click', this.#onClick);
    document.removeEventListener('variant:update', this.#onVariantUpdate);
  }

  #onVariantUpdate = (event) => {
    const productId = event?.detail?.data?.productId;
    if (productId && String(productId) !== String(this.dataset.productId)) return;
    const id = event?.detail?.resource?.id;
    if (!id) return;
    this.dataset.variantId = String(id);
    this.#refresh();
  };

  #refresh() {
    const variant = this.#data?.[this.dataset.variantId];
    const priceEl = this.querySelector('[data-bt-buy-once-price]');
    const btn = this.querySelector('[data-bt-buy-once-submit]');
    if (priceEl && variant?.price_formatted) {
      priceEl.textContent = ` ${variant.price_formatted}`;
    }
    if (btn instanceof HTMLButtonElement) {
      const available = variant ? Boolean(variant.available) : !btn.disabled;
      btn.disabled = !available;
      btn.toggleAttribute('aria-disabled', !available);
    }
  }

  #getForm() {
    const root = this.closest('.shopify-section') || document;
    return (
      root.querySelector(
        `product-form-component[data-product-id="${this.dataset.productId}"] form[data-type="add-to-cart-form"]`
      ) || document.querySelector('product-form-component form[data-type="add-to-cart-form"]')
    );
  }

  #onClick = async (event) => {
    event.preventDefault();
    if (this.#busy) return;

    const btn = this.querySelector('[data-bt-buy-once-submit]');
    if (btn instanceof HTMLButtonElement && btn.disabled) return;

    const form = this.#getForm();
    if (!(form instanceof HTMLFormElement)) return;

    const productForm = form.closest('product-form-component');
    this.#busy = true;
    if (btn instanceof HTMLButtonElement) btn.setAttribute('aria-busy', 'true');

    // Snapshot + remove selling_plan so this add is a true one-time purchase
    const planInputs = Array.from(form.querySelectorAll('input[name="selling_plan"]'));
    const snapshot = planInputs.map((input) => ({
      input,
      parent: input.parentNode,
      next: input.nextSibling,
    }));
    planInputs.forEach((input) => input.remove());

    try {
      const formData = new FormData(form);
      formData.delete('selling_plan');

      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });
      if (sectionIds.length) {
        formData.set('sections', sectionIds.join(','));
      }

      const cartAddUrl =
        window.Theme?.routes?.cart_add_url ||
        `${window.Shopify?.routes?.root || '/'}cart/add.js`;

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      }).then((res) => res.json());

      if (response.status) {
        console.warn('[BT Buy Once]', response.message || response.description || response);
        document.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: {
              sourceId: this.id || 'bt-buy-once',
              message: response.message,
              description: response.description,
              errors: response.errors,
            },
          })
        );
        return;
      }

      const variantId = String(formData.get('id') || this.dataset.variantId || '');
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: response,
            sourceId: productForm?.id || 'bt-buy-once',
            data: {
              source: 'bt-buy-once',
              productId: this.dataset.productId,
              variantId,
              itemCount: Number(formData.get('quantity')) || 1,
              sections: response.sections,
            },
          },
        })
      );
    } catch (err) {
      console.warn('[BT Buy Once] Add to cart failed', err);
    } finally {
      // Restore subscription selling_plan inputs for the primary ATC
      snapshot.forEach(({ input, parent, next }) => {
        if (!parent) return;
        if (next) parent.insertBefore(input, next);
        else parent.appendChild(input);
      });
      this.#busy = false;
      if (btn instanceof HTMLButtonElement) btn.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('bt-buy-once-link')) {
  customElements.define('bt-buy-once-link', BtBuyOnceLink);
}
